import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRoutes } from '../global-misc/api-routes';
import { Permission, UserOrgPermission } from '../global-data-models/permission.model';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  constructor(private http: HttpClient) {}

  async getOupByUsers(orgId: string) {
    const userOrgPerms = await this.getOrgPermissions(orgId);
    const usersArr: { userId: string; name: string; email: string; permissions: number[] }[] = [];

    userOrgPerms.forEach((uop) => {
      const userId = uop.user.id;
      const existingUser = usersArr.find((u) => u.userId === userId);

      if (existingUser) {
        existingUser.permissions.push(uop.permission_id);
        return;
      }

      usersArr.push({
        userId,
        name: `${uop.user.firstName} ${uop.user.lastName}`,
        email: uop.user.email,
        permissions: [uop.permission_id],
      });
    });

    return usersArr;
  }

  async getIndividualUserOrgPermissions(orgId: string, userId: string): Promise<number[]> {
    const allUsers = await this.getOupByUsers(orgId);
    const user = allUsers.find((u) => u.userId === userId);
    if (!user) {
      throw new Error('User not found');
    }
    const permissions = user.permissions;
    return permissions;
  }

  private async getOrgPermissions(orgId: string): Promise<UserOrgPermission[]> {
    const url = APIRoutes.ORG_PERMISSIONS_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.get<UserOrgPermission[]>(url));
  }

  async getAllOrgLevelPermissions(): Promise<Permission[]> {
    return await firstValueFrom(this.http.get<Permission[]>(APIRoutes.ORG_LEVEL_PERMISSIONS_ROUTE));
  }

  async deleteUserOrgPermission(orgId: string, userId: string, permissionId: number): Promise<void> {
    const userOrgPerms = await this.getOrgPermissions(orgId);
    const permissionToDelete = userOrgPerms.find((uop) => uop.user.id === userId && uop.permission_id === permissionId);

    if (!permissionToDelete) {
      throw new Error('Permission not found');
    }
    const uopId = permissionToDelete.id;
    const url = APIRoutes.SINGLE_ORG_PERMISSIONS_ROUTE.replace(':orgId', orgId).replace(':uopId', uopId.toString());

    return await firstValueFrom(this.http.delete<void>(url));
  }

  async createUserOrgPermission(orgId: string, userId: string, permissionId: number): Promise<void> {
    const url = APIRoutes.ORG_PERMISSIONS_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.post<void>(url, { user_id: userId, permission_id: permissionId }));
  }

  async deleteMePermission(uopId: number): Promise<void> {
    const url = APIRoutes.ME_REMOVE_SINGLE_PERMISSIONS_ROUTE.replace(':uopId', uopId.toString());
    return await firstValueFrom(this.http.delete<void>(url));
  }
}
